import React, { useEffect, useRef, useState, useCallback } from "react";
import styles from "./AddCustomerForm.module.scss";
import { Modal, Row, Col, message } from "antd";
import {
  AccLinkModalConsts,
  AddCustomerConsts,
  addCustomerFields,
  getAddCustomerFields,
} from "./constants";
import { InputfieldTypes } from "../../../enums/InputFieldAttributesEnums";
import InputField from "../../../reusable-components/TNInputField";
import TNGoogleAddressSuggestions from "../../../reusable-components/TNGoogleSearch";
import { CommonButtonTexts } from "../../../enums/ButtonAttributesEnums";
import { formatPhoneNumber } from "../../../utils/numberFormatterUtil";
import { useFormContext } from "react-hook-form";
import CustomerDatabaseService from "../../../services/customerDatabase.service";
import TNLoader from "../../../reusable-components/TNLoader";
import { useQueryClient } from "@tanstack/react-query";
import { QueryKeys } from "../../../constants/QueryKeys";
import { useHistory } from "react-router-dom";

interface AddCustomerFormProps {
  isOpen: boolean;
  handleClose: () => void;
  isEdit: boolean;
  customerId?: string;
  searchValue?: string | undefined;
  setSearchValue?: (string) => void;
  profileView?: boolean;
}

const AddCustomerForm = ({
  isOpen,
  handleClose,
  isEdit,
  customerId,
  searchValue,
  setSearchValue,
  profileView,
}: AddCustomerFormProps) => {
  const isMountRef = useRef<boolean>(true);
  const navigate = useHistory();
  const { watch, setValue, reset, getValues, handleSubmit } = useFormContext();
  const queryClient = useQueryClient();
  const [paymentOptions, setPaymentOptions] = useState([]);
  const { ADD_MODAL_TITLE, EDIT_MODAL_TITLE, MODAL_WIDTH, CUSTOM_INPUT, CASH } =
    AddCustomerConsts;
  const {
    GetSpecificCustomerDetailsForEdit,
    CreateCustomerProfile,
    UpdateCustomerProfile,
    GetCustomerDetailsFromGoogle,
    GetAccountTermsOptions,
  } = CustomerDatabaseService();

  const { mutateAsync: createCustomer, isPending: createCustomerPending } =
    CreateCustomerProfile();
  const { mutateAsync: updateCustomer, isPending: updateCustomerPending } =
    UpdateCustomerProfile();
  const {
    mutateAsync: fetchCustomerDetails,
    isPending: fetchCustomerDetailsPending,
  } = GetSpecificCustomerDetailsForEdit();
  const {
    mutateAsync: getDetailsFromGoogle,
    isPending: detailsFromGooglePending,
  } = GetCustomerDetailsFromGoogle();
  const {
    mutateAsync: fetchAccountTerms,
    isPending: fetchAccountTermsPending,
  } = GetAccountTermsOptions({ isCrm: false });

  const accountTermsValue = watch(addCustomerFields.ACCOUNT_TERMS_NAME);

  const [accountLinkObject, setAccountLinkObject] = useState({});
  const [isConfirmModalVisible, setIsConfirmModalVisible] =
    useState<boolean>(false);

  const handleConfirmModalOpen = () => setIsConfirmModalVisible(true);
  const handleConfirmModalClose = () => {
    if (
      accountLinkObject?.name &&
      !!!getValues(addCustomerFields.CUSTOMER_NAME_NAME)
    )
      setValue(addCustomerFields.CUSTOMER_NAME_NAME, accountLinkObject?.name);
    if (accountLinkObject?.email && !!!getValues(addCustomerFields.EMAIL_NAME))
      setValue(addCustomerFields.EMAIL_NAME, accountLinkObject?.email);
    if (accountLinkObject?.phone && !!!getValues(addCustomerFields.PHONE_NAME))
      setValue(addCustomerFields.PHONE_NAME, accountLinkObject?.phone);
    if (
      accountLinkObject?.address &&
      !!!getValues(addCustomerFields.COMPANY_ADDRESS_NAME)
    ) {
      setValue(
        addCustomerFields.COMPANY_ADDRESS_NAME,
        accountLinkObject?.address
      );
      setValue(
        addCustomerFields.ADDRESS_DATA_NAME,
        accountLinkObject?.location
      );
    }
    setIsConfirmModalVisible(false);
  };

  const handleAccountLinkConfirm = () => {
    if (accountLinkObject?.name)
      setValue(addCustomerFields.CUSTOMER_NAME_NAME, accountLinkObject?.name);
    if (accountLinkObject?.email)
      setValue(addCustomerFields.EMAIL_NAME, accountLinkObject?.email);
    if (accountLinkObject?.phone)
      setValue(addCustomerFields.PHONE_NAME, accountLinkObject?.phone);
    if (accountLinkObject?.address) {
      setValue(
        addCustomerFields.COMPANY_ADDRESS_NAME,
        accountLinkObject?.address
      );
      setValue(
        addCustomerFields.ADDRESS_DATA_NAME,
        accountLinkObject?.location
      );
    }
    handleConfirmModalClose();
  };

  const fillFromAccountLink = (data) => {
    if (!!data?.name)
      setValue(addCustomerFields.CUSTOMER_NAME_NAME, data?.name);
    if (!!data?.email) setValue(addCustomerFields.EMAIL_NAME, data?.email);
    if (!!data?.phone) setValue(addCustomerFields.PHONE_NAME, data?.phone);
    if (!!data?.address) {
      setValue(addCustomerFields.COMPANY_ADDRESS_NAME, data?.address);
      setValue(addCustomerFields.ADDRESS_DATA_NAME, data?.location);
    }
  };

  const handleFormCancel = () => {
    setAccountLinkObject(null);
    reset();
    handleClose();
  };

  const handleFormSubmit = async () => {
    const payload = {
      ...accountLinkObject,
      accountLink:
        !!!getValues(addCustomerFields.ACCOUNT_LINK_NAME) && isEdit
          ? ""
          : getValues(addCustomerFields.ACCOUNT_LINK_NAME),
      companyName: getValues(addCustomerFields.CUSTOMER_NAME_NAME)?.trim(),
      accountTerms:
        getValues(addCustomerFields.ACCOUNT_TERMS_NAME) ===
        AddCustomerConsts.CUSTOM_INPUT
          ? getValues(addCustomerFields.CUSTOM_TERMS_NAME)
          : !!!getValues(addCustomerFields.ACCOUNT_TERMS_NAME) && isEdit
          ? ""
          : getValues(addCustomerFields.ACCOUNT_TERMS_NAME),
      email: getValues(addCustomerFields.EMAIL_NAME),
      phone: getValues(addCustomerFields.PHONE_NAME),
      address: getValues(addCustomerFields.COMPANY_ADDRESS_NAME),
      lat: getValues(addCustomerFields.ADDRESS_DATA_NAME)?.lat,
      lng: getValues(addCustomerFields.ADDRESS_DATA_NAME)?.lng,
      notes: getValues(addCustomerFields.NOTES_FIELD_NAME),
    };
    delete payload.name;
    delete payload?.location;
    if (isEdit) {
      delete payload.location;
      await updateCustomer(
        { payloadData: payload, customerId: customerId },
        {
          onSuccess: () => {
            if (!!searchValue) {
              setSearchValue(undefined);
            }
            message.success(AddCustomerConsts.EDIT_SUCCESS);
            if (profileView) {
              const locationProp = {
                customerName: payload?.companyName,
              };
              navigate.replace(window.location.pathname, locationProp);
            }
            queryClient.invalidateQueries([QueryKeys.GET_CUSTOMER_PROFILE_KEY]);
            queryClient.invalidateQueries([
              QueryKeys.GET_CUSTOMER_DATABASE_KEY,
            ]);
          },
          onError: (error) => {
            message.error(error?.response?.data?.message);
            console.error(`Error, ${error?.response}`);
          },
        }
      );
    } else {
      await createCustomer(payload, {
        onSuccess: () => {
          message.success(AddCustomerConsts.CREATE_SUCCESS);
          queryClient.invalidateQueries([QueryKeys.GET_CUSTOMER_DATABASE_KEY]);
        },
        onError: (error) => {
          message.error(error?.response?.data?.message);
          console.error(`Error, ${error}`);
        },
      });
    }
    setAccountLinkObject(null);
    reset();
    handleClose();
  };

  const handlePhoneChange = useCallback((e) => {
    const formattedNumber = formatPhoneNumber(e);
    setValue(addCustomerFields.PHONE_NAME, formattedNumber);
  }, []);

  const handleLinkBlur = useCallback(async (e) => {
    const { customerName, emailAddress, officePhone, addressField } =
      getValues();
    const directFill =
      !!!customerName && !!!emailAddress && !!!officePhone && !!!addressField;
    const url = e.target.value;
    if (!!!url.trim()) {
      return;
    }
    if (document.activeElement !== e.target) {
      const hide = message.loading(AddCustomerConsts.FETCHING_DATA, 0);
      await getDetailsFromGoogle(url, {
        onSuccess: (data) => {
          setAccountLinkObject(data);
          hide?.();
          if (directFill) fillFromAccountLink(data);
          else handleConfirmModalOpen();
        },
        onError: (data) => {
          hide?.();
          message.error(AddCustomerConsts.ACCOUNT_LINK_FAIL);
          console.error(`Error, ${data}`);
        },
      });
    }
  }, []);

  const handleFetchCustomerDetails = async () => {
    await fetchCustomerDetails(customerId, {
      onSuccess: (data) => {
        setAccountLinkObject({
          companyName: data?.companyName,
          email: data?.email,
          phone: data?.phone,
          address: data?.address,
          lat: data?.lat,
          lng: data?.lng,
          accountTerms: data?.accountTerms,
          openingHours: data?.openingHours,
          notes: data?.notes,
          rating: data?.rating,
          photoURL: data?.photoURL,
          redirectLink: data?.redirectLink,
          website: data?.website,
          accountLink: data?.accountLink,
        });
        setValue(addCustomerFields.NOTES_FIELD_NAME, data?.notes);
        setValue(addCustomerFields.CUSTOMER_NAME_NAME, data?.companyName);
        setValue(addCustomerFields.EMAIL_NAME, data?.email);
        setValue(addCustomerFields.PHONE_NAME, data?.phone);
        setValue(addCustomerFields.COMPANY_ADDRESS_NAME, data?.address);
        setValue(addCustomerFields.ADDRESS_DATA_NAME, {
          lat: data?.lat,
          lng: data?.lng,
        });
        if (!!data?.accountTerms)
          setValue(addCustomerFields?.ACCOUNT_TERMS_NAME, data?.accountTerms);
        if (data?.accountLink)
          setValue(addCustomerFields?.ACCOUNT_LINK_NAME, data?.accountLink);
      },
    });
  };

  const handleFetchPaymentTerms = async () => {
    await fetchAccountTerms("", {
      onSuccess: (data) => {
        setPaymentOptions(data);
      },
    });
  };

  useEffect(() => {
    if (!!isOpen) {
      handleFetchPaymentTerms();
      if (!!isEdit) {
        handleFetchCustomerDetails();
      }
    }
  }, [isOpen]);

  return (
    <>
      <TNLoader
        isLoading={
          createCustomerPending ||
          updateCustomerPending ||
          fetchAccountTermsPending ||
          fetchCustomerDetailsPending
        }
      />
      <Modal
        visible={isOpen}
        title={isEdit ? EDIT_MODAL_TITLE : ADD_MODAL_TITLE}
        width={MODAL_WIDTH}
        okText={CommonButtonTexts.SUBMIT}
        onOk={handleSubmit(handleFormSubmit)}
        onCancel={handleFormCancel}
        className={styles.addCustomerModal}
        maskClosable={false}
        okButtonProps={{
          disabled: detailsFromGooglePending,
        }}
      >
        {
          <Row>
            {getAddCustomerFields({
              onPhoneNumberChange: handlePhoneChange,
              customTermsSelected: accountTermsValue === CUSTOM_INPUT,
              onLinkBlur: handleLinkBlur,
              paymentOptions: paymentOptions,
            })?.map((item) => {
              if (item.visible) {
                if (item.type === InputfieldTypes.GOOGLE) {
                  return (
                    <Col span={item.span}>
                      <TNGoogleAddressSuggestions
                        name={item.name}
                        label={item.label}
                        requiredField={item.required}
                        isFilterField={item.isFilterField}
                        placeholder={item.placeholder}
                      />
                    </Col>
                  );
                } else {
                  return (
                    <Col span={item.span}>
                      <InputField
                        type={item.type}
                        name={item.name}
                        label={item.label}
                        formattedNumberType={item.formattedNumberType}
                        prefix={item.prefix}
                        large={item.large}
                        requiredField={item.required}
                        options={item.options}
                        placeholder={item.placeholder}
                        onChange={item.onChange}
                        onBlur={item.onBlur}
                        maxLength={item.maxLength}
                        textAreaRows={item.textAreaRows}
                        noResize={item.noResize}
                        allowClear={item.allowClear}
                        showSearch={item?.showSearch}
                      />
                    </Col>
                  );
                }
              } else {
                return <></>;
              }
            })}
          </Row>
        }
      </Modal>
      <Modal
        visible={isConfirmModalVisible}
        title={AccLinkModalConsts.TITLE}
        width={AccLinkModalConsts.WIDTH}
        style={AccLinkModalConsts.STYLE_TOP}
        okText={CommonButtonTexts.CONFIRM}
        onCancel={handleConfirmModalClose}
        onOk={handleAccountLinkConfirm}
        maskClosable={false}
      >
        {AccLinkModalConsts.CONTENT}
      </Modal>
    </>
  );
};

export default AddCustomerForm;
